import React from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Button,
  Flex,
  Badge,
  Progress,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { FiUpload, FiLink, FiMessageCircle, FiZap, FiArrowRight, FiHelpCircle, FiCheckCircle, FiPlayCircle } from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { onboardingApi } from '../services/api';

// Passo de "primeiros passos". Quando concluído, ganha selo verde e borda destacada.
const Step = ({
  n,
  icon,
  title,
  desc,
  to,
  cta,
  done,
}: {
  n: number;
  icon: any;
  title: string;
  desc: string;
  to: string;
  cta: string;
  done?: boolean;
}) => (
  <Box
    borderWidth="1px"
    borderColor={done ? 'green.300' : 'gray.200'}
    borderRadius="xl"
    bg={done ? 'green.50' : 'white'}
    p={5}
    shadow="sm"
  >
    <HStack mb={2} spacing={3}>
      <Flex
        w={8}
        h={8}
        borderRadius="full"
        bg={done ? 'green.500' : 'brand.500'}
        color="white"
        align="center"
        justify="center"
        fontWeight="bold"
      >
        {done ? <Icon as={FiCheckCircle} boxSize={5} /> : n}
      </Flex>
      <Icon as={icon} boxSize={5} color={done ? 'green.500' : 'brand.500'} />
      <Text fontWeight="bold">{title}</Text>
      {done && <Badge colorScheme="green" ml="auto">Concluído</Badge>}
    </HStack>
    <Text fontSize="sm" color="gray.600" mb={4}>{desc}</Text>
    <Button
      as={RouterLink}
      to={to}
      size="sm"
      colorScheme={done ? 'green' : 'brand'}
      variant="outline"
      rightIcon={<FiArrowRight />}
    >
      {cta}
    </Button>
  </Box>
);

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const firstName = (user?.name || '').split(' ')[0];

  // Estado real de ativação (o que já foi feito).
  const { data: status, isLoading } = useQuery('onboarding-status', onboardingApi.status, {
    staleTime: 15000,
  });

  // Carrega a base de exemplo e leva direto ao chat com ela selecionada.
  const sampleMut = useMutation(onboardingApi.loadSampleBase, {
    onSuccess: (r) => {
      queryClient.invalidateQueries('onboarding-status');
      queryClient.invalidateQueries('collections');
      navigate('/chat', { state: { preselect: r.display_name, suggestions: r.suggested_questions } });
    },
    onError: () => {
      toast({ title: 'Não foi possível carregar a base de exemplo', description: 'Tente novamente em instantes.', status: 'error', duration: 4000 });
    },
  });

  const completed = status?.completed ?? 0;
  const total = status?.total ?? 3;
  const allDone = completed >= total;
  // Mantém o atalho da base de exemplo até o usuário fazer a primeira pergunta.
  const showSample = !isLoading && status && !status.has_question;

  return (
    <Box>
      <VStack align="flex-start" spacing={1} mb={2}>
        <Heading size="lg" color="brand.700">
          Olá{firstName ? `, ${firstName}` : ''}! Bem-vindo.
        </Heading>
        <Text color="gray.600" fontSize="lg">
          Aqui você reúne o conhecimento da sua empresa num lugar só e pergunta o que quiser — com respostas que citam a fonte.
        </Text>
      </VStack>

      {/* Atalho de ativação: experimentar sem precisar subir nada */}
      {showSample && (
        <Box
          mt={6}
          borderWidth="1px"
          borderColor="brand.300"
          borderRadius="xl"
          bgGradient="linear(to-r, brand.50, white)"
          p={6}
          shadow="sm"
        >
          <HStack align="flex-start" spacing={4}>
            <Icon as={FiPlayCircle} boxSize={8} color="brand.500" mt={1} />
            <Box flex={1}>
              <Heading size="md" color="brand.700">Veja funcionando em 30 segundos</Heading>
              <Text color="gray.600" fontSize="sm" mt={1} mb={4}>
                Carregamos uma base de exemplo com um contrato pronto. Você faz uma pergunta e recebe a resposta
                com a fonte citada — sem precisar enviar nenhum documento agora.
              </Text>
              <Button
                colorScheme="brand"
                rightIcon={<FiArrowRight />}
                onClick={() => sampleMut.mutate()}
                isLoading={sampleMut.isLoading}
                loadingText="Preparando exemplo"
              >
                {status?.sample_exists ? 'Abrir base de exemplo' : 'Experimentar com base de exemplo'}
              </Button>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Progresso do checklist */}
      <Box mt={8} mb={4}>
        <Flex justify="space-between" align="center" mb={2}>
          <Heading size="md" color="gray.700">Primeiros passos</Heading>
          {!isLoading && (
            <Text fontSize="sm" color={allDone ? 'green.600' : 'gray.500'} fontWeight="medium">
              {allDone ? 'Tudo pronto!' : `${completed} de ${total} concluídos`}
            </Text>
          )}
        </Flex>
        {isLoading ? (
          <Spinner size="sm" color="brand.500" />
        ) : (
          <Progress value={(completed / total) * 100} size="sm" colorScheme={allDone ? 'green' : 'brand'} borderRadius="full" />
        )}
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Step
          n={1}
          icon={FiUpload}
          title="Envie seus documentos"
          desc="Adicione contratos, propostas, planilhas, PDFs e até áudios. O sistema lê tudo e deixa pesquisável."
          to="/upload"
          cta="Enviar documentos"
          done={status?.has_knowledge}
        />
        <Step
          n={2}
          icon={FiLink}
          title="Conecte suas fontes"
          desc="Ligue o WhatsApp, Google Drive, SharePoint ou seu sistema interno para o conhecimento entrar sozinho, todo dia."
          to="/integrations"
          cta="Conectar fontes"
          done={status?.has_source}
        />
        <Step
          n={3}
          icon={FiMessageCircle}
          title="Converse com seu conhecimento"
          desc="Pergunte qualquer coisa — 'qual o CNPJ da empresa?', 'o que foi combinado com o cliente X?' — e receba a resposta com a fonte."
          to="/chat"
          cta="Fazer uma pergunta"
          done={status?.has_question}
        />
        <Step
          n={4}
          icon={FiZap}
          title="Crie automações"
          desc="Faça a IA trabalhar sozinha: 'quando chegar uma mensagem nova, resuma e me mande no WhatsApp'."
          to="/automations"
          cta="Criar automação"
        />
      </SimpleGrid>

      {/* Glossário rápido — tira a dúvida dos termos */}
      <Box mt={8} borderWidth="1px" borderRadius="xl" bg="gray.50" p={5}>
        <HStack mb={3}>
          <Icon as={FiHelpCircle} color="brand.500" />
          <Text fontWeight="bold">Entendendo os termos</Text>
        </HStack>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} fontSize="sm" color="gray.600">
          <Box>
            <Badge colorScheme="brand" mb={1}>Base de conhecimento</Badge>
            <Text>Uma "pasta de conhecimento" — onde os documentos de um assunto ou área ficam organizados (ex.: Jurídico, Comercial). Você cria e seleciona ao enviar documentos ou no Assistente.</Text>
          </Box>
          <Box>
            <Badge colorScheme="brand" mb={1}>Fonte / Integração</Badge>
            <Text>De onde o conhecimento vem automaticamente: WhatsApp, Drive, SharePoint, banco de dados.</Text>
          </Box>
          <Box>
            <Badge colorScheme="brand" mb={1}>Automação</Badge>
            <Text>Uma tarefa que a IA faz sozinha quando algo acontece, e te entrega o resultado por WhatsApp ou e-mail.</Text>
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default Home;
